import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useAppDispatch } from 'store';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';

import { buildWeightBlurb } from '../blurbs';
import { validateStepItems } from '../validate-step';
import { readNurseMaDraft, writeNurseMaDraft } from '../draft';
import { weightCriticalReviewConfig } from './critical-review';
import { weightMonthlyTouchpointConfig } from './monthly-touchpoint';

import type { ICriticalReadingTemplateAnswers } from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ICriticalReadingData } from '../types';

interface IProps {
  patientGuid: string;
  idx: string | number;
}

/**
 * Weight Management review logic. Auto-detects monthly-interaction status to pre-fill
 * the "Routine Monthly Interaction this month" field, opens the Monthly Touchpoint when
 * the MA chooses "attempt now", and writes the weight note. The monthly interaction is
 * NOT recorded here: the touchpoint answers ride on the action and are sent with the
 * comment, and the server flips the status only once that comment is saved.
 */
export const useWeightLogic = ({ patientGuid, idx }: IProps) => {
  const {
    activityActions: { updateAction },
  } = useAppDispatch();
  const { patientInfo } = usePatientInfo({ patientGuid });

  const formik = useFormik<ICriticalReadingData>({
    initialValues: readNurseMaDraft(idx),
    onSubmit: () => {},
    validateOnChange: false,
  });
  const { setFieldValue } = formik;

  // Pre-fill the monthly-interaction field from the system-detected status once loaded:
  // interactionStatus === true ⇒ Completed, otherwise ⇒ Not completed (attempt now).
  const interactionStatus = patientInfo?.interactionStatus;
  const appliedInteractionRef = useRef(false);
  useEffect(() => {
    if (interactionStatus === undefined || appliedInteractionRef.current) {
      return;
    }
    appliedInteractionRef.current = true;
    if (formik.values.weightMonthlyInteraction === undefined) {
      setFieldValue(
        'weightMonthlyInteraction',
        interactionStatus ? 'completed' : 'attempt-now',
      );
    }
  }, [interactionStatus, setFieldValue]);

  const isAttemptNow = formik.values.weightMonthlyInteraction === 'attempt-now';
  // Touchpoint is shown only when the MA is attempting the interaction now.
  const [showTouchpoint, setShowTouchpoint] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  /** Validate the visible required fields (Critical Review + Touchpoint when shown). */
  const validate = (): string[] => {
    const items = [
      ...weightCriticalReviewConfig,
      ...(isAttemptNow && showTouchpoint ? weightMonthlyTouchpointConfig : []),
    ];
    const found = validateStepItems(items, formik.values);
    setErrors(found);
    return found;
  };

  const handleSave = async (): Promise<boolean> => {
    if (validate().length > 0) return false;
    // Touchpoint details belong on the note only when the MA actually attempted
    // it now (clicked "Attempt now"), not merely because the field pre-selected it.
    const attemptedTouchpoint = isAttemptNow && showTouchpoint;
    const blurb = buildWeightBlurb(formik.values, attemptedTouchpoint);
    // Sent with the comment; the server decides whether it counts as the monthly
    // interaction. Always overwrite, so a re-save without the touchpoint replaces an
    // earlier "reached". Consent only counts when it came from the touchpoint.
    const { consent } = formik.values;
    const criticalReadingTemplate: ICriticalReadingTemplateAnswers = {
      template: 'nurse-ma-weight',
      contact: attemptedTouchpoint ? 'reached' : 'not-reached',
      ...(attemptedTouchpoint && consent && { consent }),
    };
    updateAction({ idx, action: { message: blurb, criticalReadingTemplate } });
    writeNurseMaDraft(idx, formik.values);
    return true;
  };

  return {
    formik,
    isAttemptNow,
    showTouchpoint,
    setShowTouchpoint,
    handleSave,
    errors,
  };
};
